import { Button, ButtonGroup } from "react-bootstrap";
import { TooltipProvider } from "@bagel-web/components";
import { CommonAgentMomentList } from "../types";
import { QueryObserverResult } from "@tanstack/react-query";
import { formatTimestamp } from "../util";
import { AgentHistoryMoment } from "./AgentHistory";

const getMomentLabel = (momentId: number | null) => {
  return momentId !== null ? `Moment ${momentId}` : "--";
};

const getFirstMoment = (
  momentList?: CommonAgentMomentList
): AgentHistoryMoment | null => {
  const firstMoment = momentList?.moments[0];
  return firstMoment
    ? {
        momentId: firstMoment.moment_id,
        foreignSessionId: firstMoment.session_id,
      }
    : null;
};

const getLastMoment = (
  momentList?: CommonAgentMomentList
): AgentHistoryMoment | null => {
  const lastMoment = momentList?.moments[momentList.moments.length - 1];
  return lastMoment
    ? {
        momentId: lastMoment.moment_id,
        foreignSessionId: lastMoment.session_id,
      }
    : null;
};

const getNextMoment = (
  momentId: number | null,
  momentList?: CommonAgentMomentList
): AgentHistoryMoment | null => {
  if (!momentList) {
    return null;
  }
  const index = momentList?.moments.findIndex(
    (moment) => moment.moment_id === momentId
  );
  const nextMoment = index !== -1 ? momentList.moments[index + 1] : null;
  return nextMoment
    ? {
        momentId: nextMoment.moment_id,
        foreignSessionId: nextMoment.session_id,
      }
    : null;
};

const getPreviousMoment = (
  momentId: number | null,
  momentList?: CommonAgentMomentList
): AgentHistoryMoment | null => {
  if (!momentList) {
    return null;
  }
  const index = momentList?.moments.findIndex(
    (moment) => moment.moment_id === momentId
  );
  const prevMoment = index !== -1 ? momentList.moments[index - 1] : null;
  return prevMoment
    ? {
        momentId: prevMoment.moment_id,
        foreignSessionId: prevMoment.session_id,
      }
    : null;
};

const AgentMomentNavigator = ({
  momentId,
  momentList,
  momentListPending,
  setMomentId,
  refetchMomentList,
  isPrimary = true,
}: {
  momentId: number | null;
  momentList?: CommonAgentMomentList;
  momentListPending: boolean;
  setMomentId: (
    moment: {
      momentId: number | null;
      foreignSessionId?: string;
    } | null
  ) => void;
  refetchMomentList: () => Promise<QueryObserverResult<CommonAgentMomentList>>;
  isPrimary?: boolean;
}) => {
  const allDisabled =
    !momentList ||
    momentList.moments.length === 0 ||
    momentId === null ||
    momentListPending;

  const firstMoment = getFirstMoment(momentList);
  const nextMoment = getNextMoment(momentId, momentList);
  const prevMoment = getPreviousMoment(momentId, momentList);
  const created = momentList?.moments.find(
    (m) => m.moment_id === momentId
  )?.created;

  return (
    <div
      style={{
        display: "flex",
        gap: "5px",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
    >
      <ButtonGroup>
        <TooltipProvider tooltip={`Earliest`}>
          <Button
            className="bi-chevron-bar-left"
            variant="secondary"
            data-testid="moment-navigator-first"
            onClick={() => {
              setMomentId(firstMoment);
            }}
            disabled={allDisabled || prevMoment === null}
          />
        </TooltipProvider>
        <TooltipProvider tooltip="Previous">
          <Button
            className="bi-chevron-left"
            variant="secondary"
            data-testid="moment-navigator-previous"
            onClick={() => {
              setMomentId(prevMoment);
            }}
            disabled={allDisabled || prevMoment === null}
          />
        </TooltipProvider>
        <Button
          variant="secondary"
          disabled
          data-testid="moment-navigator-label"
        >
          {getMomentLabel(momentId)}
        </Button>
        <TooltipProvider tooltip="Next">
          <Button
            className="bi-chevron-right"
            variant="secondary"
            data-testid="moment-navigator-next"
            onClick={() => {
              setMomentId(nextMoment);
            }}
            disabled={allDisabled || nextMoment === null}
          />
        </TooltipProvider>
        <TooltipProvider tooltip={"Latest"}>
          <Button
            className="bi-chevron-bar-right"
            variant="secondary"
            data-testid="moment-navigator-latest"
            onClick={async () => {
              const result = await refetchMomentList();
              if (result.data) {
                setMomentId(getLastMoment(result.data));
              }
            }}
            disabled={allDisabled}
          />
        </TooltipProvider>
      </ButtonGroup>
      {created && isPrimary && <i>{formatTimestamp(created)}</i>}
    </div>
  );
};

export default AgentMomentNavigator;
